import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { LayoutDashboard, FileText, Images, Building2, Users, Settings as SettingsIcon, LogOut, Leaf, Menu, Inbox, PenSquare, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/appApi";
import { ROLE_LABELS } from "@/lib/adminConst";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pendingComments, setPendingComments] = useState(0);

  const isSuper = user?.role === "super_admin";
  const canWrite = ["super_admin", "publisher", "editor", "operator", "contributor"].includes(user?.role);
  const canModerate = ["super_admin", "publisher", "editor", "operator"].includes(user?.role);

  useEffect(() => {
    if (!canModerate) return;
    const load = () => api.get("/comments/pending-count").then((r) => setPendingComments(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [canModerate]);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const Nav = ({ onNavigate }) => (
    <nav className="flex flex-col gap-1 p-3">
      <Item to="/admin" end icon={LayoutDashboard} label="Dashboard" testid="menu-dashboard" onNavigate={onNavigate} />
      {canWrite && <>
        <SectionLabel>Konten</SectionLabel>
        <Item to="/admin/konten" icon={FileText} label="Daftar Berita" testid="menu-konten" onNavigate={onNavigate} />
        <Item to="/admin/konten/baru" icon={PenSquare} label="Tulis Berita" testid="menu-tulis" onNavigate={onNavigate} />
        <Item to="/admin/antrean" icon={Inbox} label="Antrean & Revisi" testid="menu-antrean" onNavigate={onNavigate} />
        <Item to="/admin/media" icon={Images} label="Media & Galeri" testid="menu-media" onNavigate={onNavigate} />
      </>}
      {canModerate && <Item to="/admin/komentar" icon={MessageSquare} label="Tanggapan Pemirsa" testid="menu-komentar" onNavigate={onNavigate} badge={pendingComments} />}
      {isSuper && <>
        <SectionLabel>Administrasi</SectionLabel>
        <Item to="/admin/organisasi" icon={Building2} label="Organisasi & Topik" testid="menu-organisasi" onNavigate={onNavigate} />
        <Item to="/admin/pengguna" icon={Users} label="Pengguna" testid="menu-pengguna" onNavigate={onNavigate} />
        <Item to="/admin/pengaturan" icon={SettingsIcon} label="Pengaturan" testid="menu-pengaturan" onNavigate={onNavigate} />
      </>}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:flex flex-col w-64 bg-dlh-greenDark text-white shrink-0 sticky top-0 h-screen overflow-y-auto">
        <SidebarHeader />
        <Nav />
        <UserBox user={user} onLogout={doLogout} />
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden bg-dlh-greenDark text-white flex items-center justify-between px-4 py-3 sticky top-0 z-40">
          <div className="flex items-center gap-2"><Leaf className="h-5 w-5 text-dlh-greenLight" /><span className="font-serif font-bold">PAPA BERSERI CMS</span></div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><button className="p-2" data-testid="admin-mobile-menu"><Menu className="h-6 w-6" /></button></SheetTrigger>
            <SheetContent side="left" className="w-72 bg-dlh-greenDark text-white p-0 border-none overflow-y-auto">
              <SheetTitle className="sr-only">Menu Admin</SheetTitle>
              <SidebarHeader />
              <Nav onNavigate={() => setOpen(false)} />
              <UserBox user={user} onLogout={doLogout} />
            </SheetContent>
          </Sheet>
        </header>
        <main className="flex-1 p-4 sm:p-8 max-w-7xl w-full mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}

function SidebarHeader() {
  return (
    <div className="p-5 border-b border-white/10 flex items-center gap-2">
      <Leaf className="h-7 w-7 text-dlh-greenLight" />
      <div><div className="font-serif font-extrabold leading-tight">PAPA BERSERI</div><div className="text-xs text-dlh-greenLight">Dashboard Pengelola</div></div>
    </div>
  );
}

function UserBox({ user, onLogout }) {
  return (
    <div className="mt-auto p-4 border-t border-white/10">
      <div className="text-sm font-semibold truncate">{user?.name}</div>
      <div className="text-xs text-dlh-greenLight mb-3">{ROLE_LABELS[user?.role] || user?.role}</div>
      <button onClick={onLogout} data-testid="logout-btn" className="w-full flex items-center gap-2 text-sm bg-white/10 hover:bg-dlh-orange px-3 py-2 rounded-lg transition-colors">
        <LogOut className="h-4 w-4" /> Keluar
      </button>
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="px-3 pt-4 pb-1 text-[11px] font-bold uppercase tracking-wider text-white/40">{children}</div>;
}

function Item({ to, end, icon: Icon, label, testid, onNavigate, badge }) {
  return (
    <NavLink to={to} end={end} onClick={onNavigate} data-testid={testid}
      className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-dlh-orange text-white" : "text-white/80 hover:bg-white/10"}`}>
      <Icon className="h-5 w-5" /> <span className="flex-1">{label}</span>
      {badge > 0 && <span data-testid="comment-badge" className="ml-auto bg-white text-dlh-greenDark text-[11px] font-bold min-w-5 h-5 px-1.5 rounded-full inline-flex items-center justify-center">{badge}</span>}
    </NavLink>
  );
}
